import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { knowledgeBasesApi, kbTagsApi } from '../utils/api';
import type { KnowledgeBaseListItem, KBTagTreeNode } from '../types/knowledgeBase';
import { useAuth } from '../utils/auth';
import Navigation from '../components/Navigation';
import TagTree, { TagBadges } from '../components/TagTree';
import { useSmartSticky } from '../hooks/useSmartSticky';

export default function KnowledgeBasesPage() {
  const { user } = useAuth();
  const [kbs, setKbs] = useState<KnowledgeBaseListItem[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [tags, setTags] = useState<KBTagTreeNode[]>([]);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [showTagSidebar, setShowTagSidebar] = useState(true);

  // Smart sticky sidebar
  const sidebarRef = useSmartSticky(96, 16);

  const isAdmin = user?.is_admin || false;

  // Handle tag selection
  const handleSelectTag = (tagId: number | null) => {
    setSelectedTagId(tagId);
    // Scroll to top to show header and start of filtered list
    window.scrollTo(0, 0);
  };

  // Get selected tag path for title (returns array of tag names from root to selected)
  const getSelectedTagPath = (): string[] | null => {
    if (selectedTagId === null) return null;
    const findTagPath = (nodes: KBTagTreeNode[], path: string[] = []): string[] | null => {
      for (const node of nodes) {
        const currentPath = [...path, node.name];
        if (node.id === selectedTagId) return currentPath;
        const found = findTagPath(node.children, currentPath);
        if (found) return found;
      }
      return null;
    };
    return findTagPath(tags);
  };

  const loadTags = async () => {
    try {
      const data = await kbTagsApi.getTree();
      setTags(data);
    } catch (err) {
      console.error('Failed to load tags:', err);
    }
  };

  const loadKnowledgeBases = async () => {
    try {
      setIsLoading(true);
      let data: KnowledgeBaseListItem[];
      if (selectedTagId !== null) {
        data = await knowledgeBasesApi.getByTag(selectedTagId, showArchived);
      } else {
        data = await knowledgeBasesApi.getAll(showArchived);
      }
      setKbs(data);
      setError('');
    } catch (err) {
      setError('Failed to load knowledge bases');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTags();
  }, []);

  useEffect(() => {
    loadKnowledgeBases();
  }, [showArchived, selectedTagId]);

  if (isLoading && kbs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading knowledge bases...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Knowledge Bases - MAIA Agent 128</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Navigation />

      <div className="max-w-7xl mx-auto px-4 pt-24 pb-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">
            {selectedTagId !== null && getSelectedTagPath() ? (
              <>
                Tag:{' '}
                {getSelectedTagPath()!.map((name, index, arr) => (
                  <span key={index}>
                    {index < arr.length - 1 ? (
                      <span className="text-lg font-normal text-gray-500">{name} / </span>
                    ) : (
                      <span>{name}</span>
                    )}
                  </span>
                ))}
              </>
            ) : (
              'Knowledge Bases'
            )}
          </h1>
          <div className="flex items-center space-x-3">
            <Link
              to="/kb-tags"
              className="px-4 py-2 btn-secondary rounded-md text-sm font-medium"
            >
              Manage Tags
            </Link>
            <Link
              to="/knowledge-bases/new"
              className="px-4 py-2 btn-primary rounded-md text-sm font-medium"
            >
              New Knowledge Base
            </Link>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Tag Sidebar */}
          <div className={`${showTagSidebar ? 'w-64' : 'w-0'} flex-shrink-0 transition-all duration-200`}>
            {showTagSidebar && (
              <div
                ref={sidebarRef}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-medium text-gray-900">Filter by Tag</h2>
                  <button
                    onClick={() => setShowTagSidebar(false)}
                    className="text-gray-400 hover:text-gray-600"
                    title="Hide sidebar"
                  >
                    ←
                  </button>
                </div>
                <TagTree
                  tags={tags}
                  selectedTagId={selectedTagId}
                  onSelectTag={handleSelectTag}
                  showAllOption={true}
                  allLabel="All Knowledge Bases"
                />
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {!showTagSidebar && (
                  <button
                    onClick={() => setShowTagSidebar(true)}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-md hover:bg-gray-50"
                  >
                    Show Tags →
                  </button>
                )}
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Show Archived</span>
                </label>
              </div>
              {selectedTagId !== null && (
                <button
                  onClick={() => setSelectedTagId(null)}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Clear filter
                </button>
              )}
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {kbs.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                <p className="text-gray-500">
                  {selectedTagId !== null
                    ? 'No knowledge bases with this tag'
                    : showArchived
                    ? 'No archived knowledge bases'
                    : 'No knowledge bases yet'}
                </p>
                {!showArchived && selectedTagId === null && (
                  <Link
                    to="/knowledge-bases/new"
                    className="inline-block mt-4 px-4 py-2 btn-primary rounded-md text-sm font-medium"
                  >
                    Create Your First Knowledge Base
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid gap-4">
                {kbs.map((kb) => (
                  <Link
                    key={kb.id}
                    to={`/knowledge-bases/${kb.id}`}
                    className="bg-white border border-gray-200 rounded-lg p-6 card-hover shadow-subtle relative"
                  >
                    {kb.archived && (
                      <div className="absolute top-4 right-4">
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          Archived
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">{kb.title}</h3>
                        {kb.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {kb.description}
                          </p>
                        )}
                        {kb.tags && kb.tags.length > 0 && (
                          <div className="mt-2">
                            <TagBadges tags={kb.tags} maxDisplay={4} />
                          </div>
                        )}
                        {isAdmin && kb.created_by_username && (
                          <p className="text-xs text-gray-500 mt-2">
                            By: {kb.created_by_fullname || kb.created_by_username} (@{kb.created_by_username})
                          </p>
                        )}
                        {kb.source_task_id && (
                          <p className="text-xs text-blue-600 mt-1">
                            From Task #{kb.source_task_id}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{kb.resource_count} resource{kb.resource_count !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Created: {new Date(kb.created_at).toLocaleDateString()}</span>
                      <span>Updated: {new Date(kb.updated_at).toLocaleDateString()}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
